import { useEffect, useRef, useState } from "react";
import { createSketchAnimation } from "./sketchAnimator";

/* ============================================================================
   The scatter

   Two kinds of placement:

   ANCHORED (anchor: "L" | "R" | "C") — positioned against the text, not the
   viewport. A guard band is measured out from the centre line in pixels, and
   these sit right up against the outside of it. This is what fills the space
   beside the headline, which percentage positioning could never reach: the
   headline is a roughly fixed pixel width, so a percentage that clears it at
   1920px lands on top of it at 1100px.

   FREE (pos) — percentage placement for everything out in the margins, where
   there is nothing to collide with.

   `order` is the draw sequence, not z-order. It works inward: margins first,
   then the pieces flanking the type, so the text is the last thing surrounded.
   ============================================================================ */
const ART = "/SketchesForWebsite";

/* Half the widest line of type, plus clearance. Capped so it stops growing on
   very wide screens — the headline stops growing at its clamp ceiling too. */
function guardPx(vw) {
  return Math.min(300, vw * 0.26) + 34;
}

const SCATTER = [
  /* ── outer margins ─────────────────────────────────────────────────────── */
  {
    file: "01_Club_Cells_Structure_Location.png",
    order: 0, size: 215, rotate: -6, mobile: true,
    pos: { left: "1%", top: "4%" },
    mobilePos: { left: "-8%", top: "3%" },
  },
  {
    file: "02_hCG_Secretion_Begins.png",
    order: 1, size: 210, rotate: 7, mobile: true,
    pos: { right: "1%", top: "3%" },
    mobilePos: { right: "-9%", top: "2%" },
  },
  {
    file: "02_Spontaneous_Beating.png",
    order: 2, size: 220, rotate: -4, mobile: true,
    pos: { left: "3%", bottom: "6%" },
    mobilePos: { left: "-7%", bottom: "7%" },
  },
  {
    file: "02_Insulin_Lipodystrophy_Hypersensitivity.png",
    order: 3, size: 225, rotate: 5, mobile: true,
    pos: { right: "2%", bottom: "5%" },
    mobilePos: { right: "-8%", bottom: "4%" },
  },

  /* ── interior fill ─────────────────────────────────────────────────────── */
  {
    file: "01_Lymph_Node_Chronic_Inflammation.png",
    order: 4, size: 180, rotate: -10, mobile: false,
    pos: { left: "3%", top: "37%" },
  },
  {
    file: "01_Superficial_Peroneal_Sensory.png",
    order: 5, size: 195, rotate: -6, mobile: false,
    pos: { left: "21%", bottom: "0%" },
  },
  {
    file: "02_Lymph_Node_Infiltration.png",
    order: 6, size: 200, rotate: 8, mobile: false,
    pos: { right: "24%", bottom: "3%" },
  },

  /* ── against the type ──────────────────────────────────────────────────── */
  {
    file: "02_Distorted_Representation_Explained.png",
    order: 7, size: 200, rotate: 3, mobile: false,
    anchor: "C", top: "1%",
  },
  {
    file: "02_Median_Nerve_Hand_Deformities.png",
    order: 8, size: 205, rotate: -3, mobile: true,
    anchor: "C", bottom: "2%",
    mobilePos: { left: "24%", bottom: "1%" },
  },
  {
    file: "01_Midbrain_Nuclei_Hub.png",
    order: 9, size: 200, rotate: -9, mobile: false,
    anchor: "L", gap: 0, top: "14%",
  },
  {
    file: "01_Superior_Ophthalmic_Vein_Intro.png",
    order: 10, size: 205, rotate: 6, mobile: false,
    anchor: "R", gap: 22, top: "10%",
  },
  {
    file: "02_Phosphate_Groups_Charge.png",
    order: 11, size: 210, rotate: 5, mobile: false,
    anchor: "L", gap: 40, top: "40%",
  },
  {
    /* Now a margin piece rather than an anchored one — it sits in the right
       column, between the uterus above and the two blobs below. That gap is
       only ~265px tall, so the size comes down to fit with clearance. */
    file: "02_Down_Syndrome_VSD_ASD.png",
    order: 12, size: 185, rotate: 7, mobile: false,
    pos: { right: "4%", top: "35%" },
  },
];

const DRAW_MS  = 1700;
const STAGGER  = 240;
const LEAD_IN  = 560;

const MOBILE_BREAKPOINT = 720;

function readLayout() {
  const vw = typeof window === "undefined" ? 1280 : window.innerWidth;
  const isMobile = vw < MOBILE_BREAKPOINT;
  const scale = isMobile
    ? Math.max(0.44, Math.min(0.60, vw / 1280))
    : Math.max(0.62, Math.min(1.25, vw / 1280));
  return { vw, isMobile, scale };
}

export default function SketchScatter() {
  const [layout, setLayout] = useState(readLayout);
  const hostRef = useRef(null);

  useEffect(() => {
    let timer = 0;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const next = readLayout();
        setLayout((prev) => {
          const bandChanged =
            prev.isMobile !== next.isMobile ||
            Math.abs(prev.scale - next.scale) > 0.08;
          const widthMoved = Math.abs(prev.vw - next.vw) > 40;
          return bandChanged || widthMoved ? next : prev;
        });
      }, 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const items = SCATTER.filter((s) => (layout.isMobile ? s.mobile : true));

  /* Anchored items resolve to a calc() against the centre line. Percentage
     items pass straight through. */
  function styleFor(item) {
    if (layout.isMobile && item.mobilePos) {
      return { ...item.mobilePos, transform: `rotate(${item.rotate}deg)` };
    }
    if (!layout.isMobile && item.anchor) {
      const guard = guardPx(layout.vw) + (item.gap || 0);
      const size = Math.round(item.size * layout.scale);
      const vertical = item.top ? { top: item.top } : { bottom: item.bottom };
      if (item.anchor === "L") {
        // Its right edge lands just outside the guard band.
        return { right: `calc(50% + ${guard}px)`, ...vertical,
                 transform: `rotate(${item.rotate}deg)` };
      }
      if (item.anchor === "R") {
        return { left: `calc(50% + ${guard}px)`, ...vertical,
                 transform: `rotate(${item.rotate}deg)` };
      }
      // "C" — centred above or below the type, where there is no line of text.
      return { left: `calc(50% - ${Math.round(size / 2)}px)`, ...vertical,
               transform: `rotate(${item.rotate}deg)` };
    }
    return { ...item.pos, transform: `rotate(${item.rotate}deg)` };
  }

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const animations = [];
    const timers = [];

    items
      .slice()
      .sort((a, b) => a.order - b.order)
      .forEach((item, i) => {
        const canvas = host.querySelector(`canvas[data-file="${item.file}"]`);
        if (!canvas) return;

        const size = Math.round(item.size * layout.scale);
        const anim = createSketchAnimation(canvas, {
          src: `${ART}/${item.file}`,
          size,
          duration: DRAW_MS,
          penWidth: Math.max(1, 1.35 * Math.min(1, layout.scale)),
          dotRadius: Math.max(3, 4.6 * Math.min(1, layout.scale)),
          traceSize: 230,
          onReady: () => {
            const holder = canvas.parentElement;
            if (holder) holder.style.opacity = "1";
          },
        });
        animations.push(anim);
        timers.push(setTimeout(() => anim.start(), LEAD_IN + i * STAGGER));
      });

    return () => {
      timers.forEach(clearTimeout);
      animations.forEach((a) => a.destroy());
    };
    // Deliberately not depending on vw: a width nudge repositions the tiles but
    // must not re-trace them, which would restart every animation mid-flight.
  }, [layout.isMobile, layout.scale]);

  return (
    <div className="scatter" ref={hostRef} aria-hidden="true">
      {items.map((item) => (
        <div key={item.file} className="scatter-item" style={styleFor(item)}>
          <canvas data-file={item.file} />
        </div>
      ))}
    </div>
  );
}
